<template>
  <q-input
    ref="inputRef"
    class="widget-field-text"
    :class="{
      [`widget-field-text--${size}`]: size,
      'widget-field-text--error': hasError,
    }"
    :model-value="modelValue"
    :placeholder="placeholder"
    :error-state="hasError"
    :bottom-slots="hasError"
    outlined
    :maxlength="maxLength"
    dense
    :readonly="readonly"
    @update:model-value="$emit('update:model-value', String($event || ''))"
    @blur="$emit('blur')"
  >
    <template
      v-if="leftIcon"
      v-slot:prepend
    >
      <slot name="prepend">
        <div class="widget-field-text__prepend">
          <widget-icon
            :name="leftIcon"
            :size="iconSize"
          />
        </div>
      </slot>
    </template>

    <template v-slot:hint>
      <widget-field-error :text="errorMessage" />
    </template>
  </q-input>
</template>

<script lang="ts">
import { widgetFieldEvents, widgetFieldProps } from '@widgets/shared/components/fields/types'
import WidgetFieldError from '@widgets/shared/components/fields/WidgetFieldError.vue'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import type { WidgetIconName } from '@widgets/shared/icons/icons'
import { computed, defineComponent, onMounted, type PropType, ref, watch } from '@/composition/vue/compositionApi'

export type WidgetFieldTextSize = '28px' | '32px' | '36px' | '40px'

export const widgetFieldTextProps = {
  placeholder: {
    type: String,
  },
  size: {
    type: String as PropType<WidgetFieldTextSize>,
    default: '40px' satisfies WidgetFieldTextSize,
  },
  maxLength: {
    type: Number,
    default: 255,
  },
  leftIcon: {
    type: String as PropType<WidgetIconName>,
  },
  autofocus: {
    type: Boolean,
  },
}

export default defineComponent({
  components: {
    WidgetIcon,
    WidgetFieldError,
  },
  props: {
    ...widgetFieldProps,
    ...widgetFieldTextProps,
  },
  emits: widgetFieldEvents,
  setup(props) {
    const inputRef = ref<HTMLInputElement>()

    const focus = () => {
      if (props.readonly) return
      inputRef.value?.focus()
    }

    const iconSizeMap: Record<WidgetFieldTextSize, string> = {
      '28px': '15px',
      '32px': '15px',
      '36px': '20px',
      '40px': '20px',
    }

    const iconSize = computed(() => iconSizeMap[props.size])

    watch(
      () => props.autofocus,
      (value) => {
        if (value) {
          focus()
        }
      },
      { flush: 'post' },
    )

    onMounted(() => {
      if (props.autofocus) {
        focus()
      }
    })

    return {
      inputRef,
      iconSize,
      focus,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/quasar-field.scss';

.widget-field-text {
  @include quasar-field-default($include-readonly: false);

  &--error {
    @include quasar-field-error;
  }

  // default style
  :deep(.q-field__control) {
    background-color: $white;
    padding: 0;

    input {
      padding: 0 13px;
    }
    i {
      color: $gray !important;
      &.success--text {
        color: $success !important;
      }
      &.light--text {
        color: $light !important;
      }
    }
  }

  &__prepend {
    margin-right: -12px;
    margin-top: -4px;
  }

  :deep(.q-field__append) {
    margin-right: 11px;
    button {
      font-size: 16px;
      color: $gray;
      transition: all $transition-300ms-ease-in-out;
      opacity: 1;
    }
  }

  //fieldText-sizes (name, height, font-size, left icon margin-left)
  $fieldText-sizes:
    '28px' 28px $sm-font 7px,
    '32px' 32px $font-size-root 7px,
    '36px' 36px $font-size-root 12px,
    '40px' 40px $font-size-root 12px;

  @each $name, $height, $font-size, $margin-left, $i-font-size in $fieldText-sizes {
    &--#{$name} {
      font-size: $font-size !important;

      :deep(.q-field__control),
      :deep(.q-field__marginal) {
        height: $height;
      }
    }

    &--#{$name} &__prepend {
      margin-left: $margin-left;
    }
  }
  &.q-field--focused :deep(.q-field__control::after) {
    border-color: $blue400;
  }
}
</style>
